import type { PlannedSlot, Task } from "./types";

/** Longest range ``plannerOccurrences`` accepts (days, both ends included). */
export const MAX_PLANNER_RANGE_DAYS = 62;

/** Days loaded on each side of the selected day. The planner is day based;
 * a window around the day lets the user step through the week without a
 * request per click, and it stays far below the server limit. */
export const PLANNER_RANGE_RADIUS_DAYS = 7;

/** One entry of ``plannerOccurrences`` as the planner reads it. Structural,
 * so the mapping can be tested without the generated types. */
export type PlannerOccurrenceInput = {
  occurrenceKey: string;
  taskId: string | null;
  seriesId: string | null;
  seriesTitle?: string | null;
  occurrenceDate?: string | null;
  plannedDate?: string | null;
  plannedUntil?: string | null;
  isVirtual: boolean;
  isMoved?: boolean;
  version?: string | null;
  isCancelled?: boolean;
  title: string;
  shortDescription?: string | null;
  description?: string | null;
  availableFrom?: string | null;
  availableUntil?: string | null;
  firstDayAvailable?: string | null;
  lastDayAvailable?: string | null;
  continuesAfterEndDate?: boolean | null;
  color?: string | null;
  plannedDurationHours?: number | null;
  plannedDurationMinutes?: number | null;
  priority?: string | null;
  responsible?: ReadonlyArray<string>;
  representatives?: ReadonlyArray<string>;
  responsibleUsers?: ReadonlyArray<{ id: string; fullName: string }>;
  representativeUsers?: ReadonlyArray<{ id: string; fullName: string }>;
  requiresTeamwork?: boolean;
  activityCostCenterIds?: ReadonlyArray<number>;
  selectedCostCenterIds?: ReadonlyArray<number>;
  status?: string | null;
  progressStatus?: string | null;
  hasPendingReview?: boolean;
  plannedSlots?: ReadonlyArray<PlannedSlot>;
};

export type PlannerRange = { fromDate: string; toDate: string };

/** A date as ``YYYY-MM-DD`` from its local parts.
 *
 * ``toISOString`` converts to UTC first and hands the server the previous
 * day for everybody east of Greenwich. */
export const toIsoDay = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(
    date.getDate(),
  ).padStart(2, "0")}`;

/** ``date`` moved by whole calendar days, at local midnight. Built from the
 * date parts, so a daylight saving switch cannot shift the day. */
export const addDays = (date: Date, days: number): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);

/** The range loaded around ``day``. */
export const computePlannerRange = (
  day: Date,
  radiusDays: number = PLANNER_RANGE_RADIUS_DAYS,
): PlannerRange => {
  // Never ask for more than the server allows.
  const radius = Math.max(
    0,
    Math.min(radiusDays, Math.floor((MAX_PLANNER_RANGE_DAYS - 1) / 2)),
  );
  return {
    fromDate: toIsoDay(addDays(day, -radius)),
    toDate: toIsoDay(addDays(day, radius)),
  };
};

/** Is ``day`` covered by the loaded range? ISO days compare as strings. */
export const isDayInRange = (day: Date, range: PlannerRange): boolean => {
  const iso = toIsoDay(day);
  return iso >= range.fromDate && iso <= range.toDate;
};

export const isSeriesRun = (task: Pick<Task, "seriesId">): boolean =>
  Boolean(task.seriesId);

const toStrings = (values?: ReadonlyArray<string | number>): string[] =>
  (values ?? []).map(String);

const toNumbers = (values?: ReadonlyArray<number>): number[] => [
  ...(values ?? []),
];

/** One ``TaskOccurrence`` as a planner card.
 *
 * The card's ``id`` is the ``occurrenceKey``, so it keeps its identity
 * when a virtual run gets stored. A series run belongs to its planned day:
 * ``plannedUntil`` is null by contract, and treating that as an open end
 * would show the run on every later day. The exception is an overdue run
 * nobody is responsible for, which the server flags with
 * ``continuesAfterEndDate`` (client decision 2026-11-02): it stays on the
 * later days until somebody takes it. Standalone tasks keep their own date
 * span; the same flag lifts their end date once closed unfinished. */
export const occurrenceToPlannerTask = (o: PlannerOccurrenceInput): Task => {
  const seriesRun = Boolean(o.seriesId);
  return {
    id: String(o.occurrenceKey),
    occurrenceKey: String(o.occurrenceKey),
    taskId: o.taskId != null ? String(o.taskId) : null,
    seriesId: o.seriesId != null ? String(o.seriesId) : null,
    seriesTitle: o.seriesTitle ?? null,
    isVirtual: Boolean(o.isVirtual),
    occurrenceDate: o.occurrenceDate ?? null,
    isMoved: Boolean(o.isMoved),
    isCancelled: Boolean(o.isCancelled),
    version: o.version ?? null,
    title: o.title,
    shortDescription: o.shortDescription ?? undefined,
    description: o.description ?? undefined,
    availableFrom: o.availableFrom ?? undefined,
    availableUntil: o.availableUntil ?? undefined,
    doFrom: o.plannedDate ?? null,
    doUntil: seriesRun ? (o.plannedDate ?? null) : (o.plannedUntil ?? null),
    firstDayAvailable: o.firstDayAvailable ?? null,
    lastDayAvailable: o.lastDayAvailable ?? null,
    continuesAfterEndDate: Boolean(o.continuesAfterEndDate),
    color: o.color ?? "",
    plannedDurationHours: o.plannedDurationHours ?? undefined,
    plannedDurationMinutes: o.plannedDurationMinutes ?? undefined,
    priority: o.priority ?? undefined,
    responsible: toStrings(o.responsible),
    representatives: toStrings(o.representatives),
    responsiblePeople: [...(o.responsibleUsers ?? [])],
    representativePeople: [...(o.representativeUsers ?? [])],
    requiresTeamwork: Boolean(o.requiresTeamwork),
    activityCostCenterIds: toNumbers(o.activityCostCenterIds),
    selectedCostCenterIds: toNumbers(o.selectedCostCenterIds),
    status: o.status ?? null,
    progressStatus: o.progressStatus ?? null,
    hasPendingReview: Boolean(o.hasPendingReview),
    plannedSlots: (o.plannedSlots ?? []).map((slot) => ({
      userId: String(slot.userId),
      day: String(slot.day),
      addedResponsible: Boolean(slot.addedResponsible),
    })),
  };
};

/** The planner's card list: cancelled runs dropped (the server already
 * leaves them out, this keeps a stale cache entry from coming back),
 * one card per ``occurrenceKey``. */
export const mapPlannerOccurrences = (
  occurrences: ReadonlyArray<PlannerOccurrenceInput>,
): Task[] => {
  const seen = new Set<string>();
  const result: Task[] = [];
  for (const occurrence of occurrences) {
    if (occurrence.isCancelled) continue;
    const key = String(occurrence.occurrenceKey);
    if (seen.has(key)) continue;
    seen.add(key);
    result.push(occurrenceToPlannerTask(occurrence));
  }
  return result;
};

/** One card of a day plan, as ``dayPlan`` returns it. */
export type DayPlanCardInput = {
  id: string;
  kind: string;
  state: string;
  occurrenceKey?: string | null;
  occurrence?: PlannerOccurrenceInput | null;
  plannedStart?: string | null;
  plannedEnd?: string | null;
};

/** Planned breaks of the day as cards for the timeline, so they sit
 * between the tasks at their time ("Pause 14:25 bis 14:50"). */
export const dayPlanBreaks = (
  cards: ReadonlyArray<DayPlanCardInput>,
): Task[] =>
  cards
    .filter((card) => card.kind === "BREAK" && card.plannedStart && card.plannedEnd)
    .map((card) => ({
      id: `break:${card.id}`,
      occurrenceKey: `break:${card.id}`,
      taskId: null,
      seriesId: null,
      isVirtual: false,
      title: "Pause",
      color: null,
      availableFrom: card.plannedStart ?? null,
      availableUntil: card.plannedEnd ?? null,
      planCardId: String(card.id),
      planState: card.state,
      planKind: "BREAK" as const,
    }));

/** The selected person's task cards for the sidebar, in plan order. A card
 * whose task no longer exists has nothing to show and is left out. */
export const dayPlanTasks = (
  cards: ReadonlyArray<DayPlanCardInput>,
): Task[] =>
  cards
    .filter((card) => card.kind === "TASK" && card.occurrence)
    .map((card) => ({
      ...occurrenceToPlannerTask(card.occurrence as PlannerOccurrenceInput),
      planCardId: String(card.id),
      planState: card.state,
    }));

/** Urlaub / Krank on the selected person's day, if any. */
export const dayPlanAbsence = (
  cards: ReadonlyArray<DayPlanCardInput>,
): { id: string; kind: "VACATION" | "SICK" } | null => {
  const card = cards.find((c) => c.kind === "VACATION" || c.kind === "SICK");
  return card ? { id: String(card.id), kind: card.kind as "VACATION" | "SICK" } : null;
};

/** Where a card opens. Series runs always open as a run (virtual or
 * stored, opening never stores anything); standalone tasks open the task.
 * Null when there is nothing to open. */
export const plannerTaskLink = (
  task: Pick<Task, "seriesId" | "occurrenceDate" | "taskId">,
  options: { reportTab?: boolean } = {},
): string | null => {
  if (task.seriesId && task.occurrenceDate) {
    return `/aufgaben/serien/${task.seriesId}/durchlauf/${task.occurrenceDate.slice(0, 10)}`;
  }
  if (task.taskId) {
    return `/aufgabe/${task.taskId}${options.reportTab ? "?tab=Report" : ""}`;
  }
  return null;
};
